import { Extension, RangeSetBuilder } from "@codemirror/state";
import {
  Decoration,
  DecorationSet,
  EditorView,
  ViewPlugin,
  ViewUpdate,
  WidgetType,
} from "@codemirror/view";

import * as styles from "./entity-chips.module.css";

export interface EntityChipEntry {
  id: string;
  name: string;
}

export interface EntityKind {
  /**
   * Unique kind identifier (e.g. "parcel", "location"). Returned in
   * onChipClick so callers can dispatch to the right picker.
   */
  kind: string;
  /** Map from lowercase id to entity. */
  entitiesById: ReadonlyMap<string, EntityChipEntry>;
}

export interface EntityChipClickInfo {
  kind: string;
  id: string;
  /**
   * Document positions of the full quoted literal (including the surrounding
   * quotes). Callers replacing the chip should re-emit the quotes.
   */
  from: number;
  to: number;
  /** The chip's DOM element — useful as a popover anchor. */
  element: HTMLElement;
}

class EntityChipWidget extends WidgetType {
  constructor(
    readonly kind: string,
    readonly id: string,
    readonly name: string | null,
    readonly clickable: boolean,
  ) {
    super();
  }
  override eq(other: EntityChipWidget): boolean {
    return (
      other.kind === this.kind &&
      other.id === this.id &&
      other.name === this.name &&
      other.clickable === this.clickable
    );
  }
  toDOM(): HTMLElement {
    const el = document.createElement("span");
    el.className = this.name
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        styles.entityChip!
      : `${styles.entityChip} ${styles.entityChipMissing}`;
    el.dataset.entityKind = this.kind;
    el.dataset.entityId = this.id;
    el.title = `${this.kind}: ${this.id}`;
    el.textContent = this.name ?? `Missing ${this.kind}`;
    if (this.clickable) {
      el.style.cursor = "pointer";
    }
    return el;
  }
  override ignoreEvent(): boolean {
    return false;
  }
}

const OBJECT_ID_LITERAL = /"([0-9a-fA-F]{24})"/g;

/**
 * Decorate string literals whose contents match a known entity id (across any
 * registered kind). The first kind whose map contains the id wins — ObjectIds
 * are globally unique so collisions across kinds aren't a real concern, but
 * the iteration order of `kinds` is the tiebreaker if it ever happens.
 */
export function entityChipsExtension(
  kinds: readonly EntityKind[],
  options: {
    showMissing?: boolean;
    onChipClick?: (info: EntityChipClickInfo) => void;
  } = {},
): Extension {
  const { showMissing = false, onChipClick } = options;
  const clickable = Boolean(onChipClick);

  const lookup = (
    id: string,
  ): { kind: string; entry: EntityChipEntry | null } | null => {
    for (const k of kinds) {
      const entry = k.entitiesById.get(id);
      if (entry) {
        return { kind: k.kind, entry };
      }
    }
    return null;
  };

  const plugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;
      constructor(view: EditorView) {
        this.decorations = this.build(view);
      }
      update(update: ViewUpdate): void {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.build(update.view);
        }
      }
      build(view: EditorView): DecorationSet {
        const builder = new RangeSetBuilder<Decoration>();
        for (const { from, to } of view.visibleRanges) {
          const text = view.state.doc.sliceString(from, to);
          OBJECT_ID_LITERAL.lastIndex = 0;
          let m: RegExpExecArray | null;
          while ((m = OBJECT_ID_LITERAL.exec(text)) !== null) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const id = m[1]!.toLowerCase();
            const hit = lookup(id);
            if (!hit && !showMissing) {
              continue;
            }
            const literalStart = from + m.index;
            const literalEnd = literalStart + m[0].length;
            builder.add(
              literalStart,
              literalEnd,
              Decoration.replace({
                widget: new EntityChipWidget(
                  hit?.kind ?? "unknown",
                  id,
                  hit?.entry?.name ?? null,
                  clickable,
                ),
              }),
            );
          }
        }
        return builder.finish();
      }
    },
    { decorations: (v) => v.decorations },
  );

  const extensions: Extension[] = [
    plugin,
    EditorView.atomicRanges.of((view) => {
      const instance = view.plugin(plugin);
      return instance ? instance.decorations : Decoration.none;
    }),
  ];

  if (onChipClick) {
    extensions.push(
      EditorView.domEventHandlers({
        click(event, view) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const target = event.target as HTMLElement | null;
          const chip = target?.closest<HTMLElement>("[data-entity-id]");
          if (!chip) {
            return false;
          }
          const id = chip.dataset.entityId;
          const kind = chip.dataset.entityKind;
          if (!id || !kind) {
            return false;
          }
          // posAtDOM returns the start of the chip's decoration range, which
          // covers the whole quoted literal: ["abc…"] — 2 quotes + 24 hex
          // chars = 26 characters.
          const from = view.posAtDOM(chip);
          const to = from + id.length + 2;
          event.preventDefault();
          event.stopPropagation();
          onChipClick({ kind, id, from, to, element: chip });
          return true;
        },
      }),
    );
  }

  return extensions;
}
